import { useCallback, useMemo } from 'react'
import { Row, Table } from '@atbay/cyber-components'
import { IntegrationsStore } from '@/core/store/Integrations/integrations.store'
import { AssetsModule } from '@core/modules/digital-footprint/assets'
import {
    countIssuesBySeverity,
    getHighestSeverity,
} from '@routes/digital-footprint/routes/assets/components/assetsTable/utils'
import {
    TAllIntegrations,
    TIntegrationStatus,
} from '@/core/modules/integrations/integrations'
import { useDrawer } from '@/core/context/DrawerContext'
import { Analytics } from '@/core/services/mixpanel'
import { getTableRowContext } from '@/ui/utils/tableUtils'
import { CLOUD_APP_INTEGRATION_TYPES } from '@/core/modules/integrations/constants'

export type TCloudAppIntegrationRow = TAssetCloudApp & {
    name: string
    type: TAllIntegrations
    status: TIntegrationStatus
    external_identifier?: string
    last_sync?: string
    numberOfOpenIssuesBySeverity: ReturnType<typeof countIssuesBySeverity>
}

const normalizeIntegrationType = (type: TAllIntegrations): TAllIntegrations => {
    if (type === 'google_fraud_defense') return 'google'
    if (type === 'ms_fraud_defense') return 'microsoft'
    return type
}

export const useCloudAppIntegrationsTable = () => {
    const { data: allAssets, loading: assetsLoading } = AssetsModule.useStore()
    const { isScansLoading } = IntegrationsStore.useStore()
    const isAssetsLoading = assetsLoading || isScansLoading
    const { setDrawerState, drawerState } = useDrawer()

    const cloudAppsAssets = useMemo(
        () =>
            allAssets.filter(
                (a) => a.asset_type === 'cloud_app'
            ) as TAssetCloudApp[],
        [allAssets]
    )

    const cloudAppIntegrations = CLOUD_APP_INTEGRATION_TYPES.flatMap((type) =>
        IntegrationsStore.getActiveAndBrokenIntegrationsByType(type)
    )

    const rows: TCloudAppIntegrationRow[] = cloudAppIntegrations.flatMap(
        (integration) => {
            const matchingApp = cloudAppsAssets.find(
                (app) => app.hostname === integration?.external_identifier
            )
            if (!matchingApp) return []

            return [
                {
                    ...matchingApp,
                    name: integration.name,
                    type: normalizeIntegrationType(integration.type),
                    status: integration.status,
                    external_identifier: integration.external_identifier,
                    last_sync: integration.last_sync,
                    numberOfOpenIssuesBySeverity: countIssuesBySeverity(
                        matchingApp.issues as TIssue[]
                    ),
                },
            ]
        }
    )

    const handleRowClick = useCallback(
        (
            row: Row<TCloudAppIntegrationRow>,
            table: Table<TCloudAppIntegrationRow>
        ) => {
            const { sortedIndex, items: sortedAssets } = getTableRowContext(
                row,
                table
            )
            const asset = row.original
            const highestSeverity = getHighestSeverity(asset.issues)

            setDrawerState((prevState) => ({
                ...prevState,
                isDrawerOpen: true,
                drawerContentType: 'assetDetails',
                contentProps: { assets: sortedAssets, index: sortedIndex },
            }))

            Analytics.event('SEM::user_clicks_at_asset', {
                origin: 'cloud_app_integrations_table',
                assetId: asset.id,
                assetType: 'cloud_app',
                severity: highestSeverity,
            })
        },
        [setDrawerState]
    )

    const isDrawerOpen = drawerState.isDrawerOpen
    const currentAssetId =
        drawerState.contentProps?.assets?.[drawerState.contentProps?.index]?.id

    const activeRowId = isDrawerOpen ? currentAssetId : undefined

    return { rows, handleRowClick, activeRowId, isAssetsLoading }
}
